import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Eye, EyeOff, UserPlus, ArrowRight, Loader2, CheckCircle2, TrendingUp,
  ShieldCheck, Sparkles
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d23d8491-b3b1-4bd6-8f0e-4d1750125554/artifacts/uxopwla6_WhatsApp%20Image%202026-03-10%20at%204.20.57%20PM.jpeg";
const HEAD = "font-['Cabinet_Grotesk',_'Manrope',_sans-serif]";

const BrandPanel = () => (
  <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950 text-white p-12 flex-col justify-between">
    {/* ambient glows */}
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#002FA7]/40 blur-3xl" />
    <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" />
    <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:48px_48px]" />

    {/* brand */}
    <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="relative flex items-center gap-3 group [perspective:600px]" data-testid="login-logo-link" title="Go to home">
      <img src={LOGO_URL} alt="StayPoint" className="w-11 h-11 rounded-xl object-contain bg-white p-0.5 shadow-md transition-transform duration-300 group-hover:[transform:rotateY(22deg)_scale(1.08)] group-hover:shadow-xl" />
      <div className="leading-tight">
        <h1 className={`${HEAD} text-xl font-extrabold tracking-tight group-hover:text-white/80 transition-colors`}>StayPoint</h1>
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">PG Management</p>
      </div>
    </Link>

    {/* center content */}
    <div className="relative max-w-md">
      <motion.span
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold mb-6"
      >
        <Sparkles className="w-3.5 h-3.5" /> Built by Owners · Trusted by Managers
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
        className={`${HEAD} text-4xl xl:text-5xl font-black tracking-tight leading-[1.05] mb-5`}
      >
        Welcome back to your<br /><span className="text-[#5B8DEF]">command center</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
        className="text-white/60 text-lg leading-relaxed"
      >
        Occupancy, rent, guests and accounting — your entire PG, hostel & co-living business in one dashboard.
      </motion.p>

      {/* floating mini dashboard card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
        className="mt-10 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md p-5 max-w-sm"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Total Collection</p>
            <p className={`${HEAD} text-2xl font-extrabold`}>₹ 8,42,500</p>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 bg-emerald-400/10 px-2.5 py-1 rounded-full">
            <TrendingUp className="w-3.5 h-3.5" /> +18%
          </span>
        </div>
        <div className="flex items-end gap-1.5 h-16">
          {[40, 65, 50, 80, 60, 95, 72].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#002FA7] to-[#5B8DEF]" style={{ height: `${h}%` }} />
          ))}
        </div>
      </motion.div>
    </div>

    {/* footer */}
    <div className="relative flex items-center gap-5 text-xs text-white/40">
      <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Bank-grade data isolation</span>
      <span>© {new Date().getFullYear()} Stay Point Private Limited</span>
    </div>
  </div>
);

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(form.email, form.password);
      toast.success('Welcome back!');
      if (userData?.role === 'super_admin') navigate('/super-admin');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA] font-['Outfit',_'IBM_Plex_Sans',_sans-serif]" data-testid="login-page">
      <BrandPanel />

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-6 sm:p-10 relative">
        <div className="absolute inset-0 lg:hidden bg-[radial-gradient(ellipse_at_top,rgba(0,47,167,0.08),transparent_60%)]" />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative w-full max-w-md"
        >
          {/* mobile brand */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={LOGO_URL} alt="StayPoint" className="w-10 h-10 rounded-xl object-contain ring-1 ring-zinc-200" />
            <h1 className={`${HEAD} text-xl font-extrabold text-zinc-950`}>StayPoint</h1>
          </div>

          <div className="rounded-3xl bg-white border border-zinc-200 shadow-[0_30px_70px_-30px_rgba(0,47,167,0.3)] p-8 sm:p-10">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#002FA7] mb-2">Sign in</p>
            <h2 className={`${HEAD} text-3xl font-bold tracking-tight text-zinc-950 mb-1.5`}>Welcome back</h2>
            <p className="text-zinc-500 mb-8">Enter your credentials to access your dashboard.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-700 font-medium">Email</Label>
                <Input
                  id="email" type="email" placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                  required data-testid="email-input"
                  style={{ textTransform: 'lowercase' }}
                  className="rounded-xl h-12 border-zinc-200 focus-visible:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-700 font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required data-testid="password-input"
                    className="rounded-xl h-12 border-zinc-200 focus-visible:ring-[#002FA7] pr-11"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#002FA7] transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit" disabled={loading} data-testid="submit-btn"
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-xl h-12 text-base font-semibold shadow-[0_12px_28px_-8px_rgba(0,47,167,0.5)] group transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-8px_rgba(0,47,167,0.6)] active:translate-y-0 active:shadow-[0_6px_14px_-4px_rgba(0,47,167,0.5)]"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Please wait...</> : <>Sign In <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
              </Button>
            </form>

            <div className="mt-7 pt-6 border-t border-zinc-100">
              <p className="text-sm text-zinc-500 text-center mb-3">Want to use StayPoint for your organisation?</p>
              <Link to="/request-access">
                <Button
                  variant="outline" data-testid="request-access-btn"
                  className="w-full rounded-xl h-11 border-zinc-300 text-[#002FA7] hover:bg-[#002FA7]/5 hover:border-[#002FA7] font-semibold"
                >
                  <UserPlus className="h-4 w-4 mr-2" /> Request Organisation Access
                </Button>
              </Link>
            </div>
          </div>

          <p className="mt-6 text-xs text-zinc-400 text-center">
            <Link to="/" className="hover:text-[#002FA7]">← Back to home</Link>
            <span className="mx-2">·</span>
            <Link to="/privacy" className="hover:text-[#002FA7]">Privacy</Link>
            <span className="mx-2">·</span>
            <Link to="/terms" className="hover:text-[#002FA7]">Terms</Link>
            <span className="mx-2">·</span>
            © {new Date().getFullYear()} Stay Point Private Limited
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
